import { inject, Injectable } from '@angular/core';
import {
  ConversationItem,
  CreateFromPublicConversationPayload,
  MessageItem,
  PublicListConversationsParams,
  SendMessageFromPublicPayload
} from '@appTypes/chat-api.types';
import { map, Observable, switchMap } from 'rxjs';
import { ChatOpenConversationParams, ChatSendMessageByReferencePayload } from '../chat.type';
import { ChatWsService } from './chat-ws.service';

@Injectable({
  providedIn: 'root'
})
export class ChatWsActionsService {
  readonly #chatWsService = inject(ChatWsService);

  public getConversation(conversationId: string) {
    return this.#chatWsService.ioRequest('chat:public/conversation/details', { params: { conversationId } });
  }
  public conversationList(query?: PublicListConversationsParams) {
    return this.#chatWsService.ioRequest('chat:public/conversation/list', { query });
  }

  public messages(conversationId: string, query?: Omit<PublicListConversationsParams, 'conversationId'>) {
    return this.#chatWsService.ioRequest('chat:public/conversation/message/list', {
      params: { conversationId },
      query
    });
  }
  public messageSend(conversationId: string, payload: SendMessageFromPublicPayload) {
    return this.#chatWsService.ioRequest('chat:public/conversation/message/send', {
      params: { conversationId },
      body: payload
    });
  }

  public conversationCreate(payload: CreateFromPublicConversationPayload) {
    return this.#chatWsService.ioRequest('chat:public/conversation/create', { body: payload });
  }

  public openConversation({
    reference,
    referenceId,
    initialMessage,
    title
  }: ChatOpenConversationParams): Observable<{ conversation: ConversationItem; messages: MessageItem[] }> {
    return this.conversationList({ reference, referenceIds: [referenceId], limit: 1, skip: 0 }).pipe(
      switchMap((response) => {
        if (response.items.length && response?.items[0]?._id) {
          return this.messages(response.items[0]._id).pipe(
            map((messages) => ({ conversation: response.items[0], messages }))
          );
        }
        return this.conversationCreate({ reference, referenceId, initialMessage, title }).pipe(
          map((conversation) => ({
            conversation,
            messages: initialMessage && conversation.lastMessage ? [conversation.lastMessage] : []
          }))
        );
      })
    );
  }
  public sendMessageByReference({
    reference,
    referenceId,
    content,
    title,
    attributes
  }: ChatSendMessageByReferencePayload): Observable<MessageItem> {
    const messagePayload: SendMessageFromPublicPayload = { content, senderTimestamp: new Date().getTime() };
    return this.conversationList({ reference, referenceIds: [referenceId] }).pipe(
      switchMap((response) => {
        if (response.items.length && response?.items[0]?._id) {
          return this.messageSend(response.items[0]._id, messagePayload);
        }
        return this.conversationCreate({
          reference,
          referenceId,
          initialMessage: messagePayload,
          title,
          attributes
        }).pipe(map((result) => result.lastMessage!));
      })
    );
  }
  public conversationMarkAsSeen(conversationId: string) {
    return this.#chatWsService.ioRequest('chat:public/conversation/mark-as-seen', {
      params: { conversationId }
    });
  }
  public unreadMessagesCount() {
    return this.#chatWsService.ioRequest('chat:public/unseen-messages-count');
  }
}
